import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Gem, Plus, Trash2, Save, Sparkles, Star } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const QualitiesExplorationPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [qualities, setQualities] = useState([{ quality: "", example: "", source: "" }]);
  const [reflection, setReflection] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const CATEGORIES = [
    { id: "interpersonal", name: t("qualities.interpersonal"), examples: t("qualities.interpersonal.ex").split(",") },
    { id: "intellectual", name: t("qualities.intellectual"), examples: t("qualities.intellectual.ex").split(",") },
    { id: "emotional", name: t("qualities.emotional"), examples: t("qualities.emotional.ex").split(",") },
    { id: "moral", name: t("qualities.moral"), examples: t("qualities.moral.ex").split(",") },
    { id: "practical", name: t("qualities.practical"), examples: t("qualities.practical.ex").split(",") },
  ];

  const addQuality = (suggestion = "") => {
    setQualities(prev => [...prev, { quality: suggestion, example: "", source: "" }]);
  };

  const removeQuality = (idx) => {
    if (qualities.length <= 1) return;
    setQualities(prev => prev.filter((_, i) => i !== idx));
  };

  const updateQuality = (idx, field, value) => {
    setQualities(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const handleSubmit = async () => {
    if (!qualities.some(q => q.quality.trim())) {
      toast.error(t("qualities.addAtLeastOne"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/qualities-exploration`, {
        qualities: qualities.filter(q => q.quality.trim()),
        reflection,
      }, { withCredentials: true });
      toast.success(t("qualities.saved"));
      if (user?.role === "patient") promptShare(res.data, "qualities_exploration");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="qualities-exploration-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("qualities.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("qualities.pageSubtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.qualities_exploration} />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Suggestions */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {t("qualities.suggestions")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {CATEGORIES.map((cat) => (
                <div key={cat.id}>
                  <button
                    onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm font-medium ${
                      selectedCategory === cat.id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                  {selectedCategory === cat.id && (
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-3">
                      {cat.examples.map((ex) => (
                        <button
                          key={ex}
                          onClick={() => addQuality(ex)}
                          className="text-xs px-2 py-1 rounded-full bg-secondary hover:bg-primary/10 transition-colors"
                        >
                          + {ex}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* My Qualities */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  {t("qualities.myQualities")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {qualities.map((q, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder={t("qualities.qualityPlaceholder")}
                          value={q.quality}
                          onChange={(e) => updateQuality(idx, "quality", e.target.value)}
                          data-testid={`quality-${idx}`}
                        />
                      </div>
                      {qualities.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeQuality(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder={t("qualities.examplePlaceholder")}
                      value={q.example}
                      onChange={(e) => updateQuality(idx, "example", e.target.value)}
                    />
                    <Input
                      placeholder={t("qualities.sourcePlaceholder")}
                      value={q.source}
                      onChange={(e) => updateQuality(idx, "source", e.target.value)}
                    />
                  </div>
                ))}

                <Button variant="outline" onClick={() => addQuality()} className="w-full" data-testid="add-quality-btn">
                  <Plus className="w-4 h-4 mr-1" /> {t("qualities.addQuality")}
                </Button>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("qualities.reflection")}</label>
                  <Textarea
                    placeholder={t("qualities.reflectionPlaceholder")}
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    rows={3}
                    data-testid="qualities-reflection"
                  />
                </div>

                <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="save-qualities">
                  <Save className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("qualities.saveQualities")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default QualitiesExplorationPage;
